'use client';
/**
 * UnifiedAnimatedSidebar
 *
 * Combined sidebar for dual filmmaker/client users — replaces the two separate
 * FilmmakerAnimatedSidebar + ClientAnimatedSidebar, removing the role-switch
 * toggle and exposing all nav items from both sides.
 *
 * Used by FullWidthDashboardLayout + ClientFullWidthDashboardLayout when
 * `userType === 'both'` so dual users see the same sidebar regardless of
 * which route they're on.
 */
import React, { useCallback } from 'react';
import {
  Sidebar,
  SidebarBody,
  SidebarLink,
} from '@/components/ui/sidebar-animated';
import {
  Briefcase,
  CalendarCheck,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  UserCircle,
  Presentation,
  ReceiptPoundSterling,
  Search,
  Users,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebarState } from '@/contexts/SidebarContext';
import { TierBadge } from '@/components/subscription/TierBadge';
import { ThemeToggleInline } from '@/components/ui/theme-toggle';

interface NavigationLink {
  label: string;
  href: string;
  icon: React.ReactNode;
  // Additional pathname prefixes that should highlight this item
  matchPrefixes?: string[];
}

// Combined nav for dual users. Order: Calendar, Find Talent, Jobs Board,
// Project Management, Bookings, Messages, Pitching Room, Invoices, Profile.
const NAVIGATION_LINKS: NavigationLink[] = [
  {
    label: 'Calendar',
    href: '/filmmaker/dashboard',
    icon: (
      <LayoutDashboard className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Find Talent',
    href: '/client/dashboard/find-talent',
    icon: (
      <Search className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Jobs Board',
    href: '/filmmaker/dashboard/jobs',
    icon: (
      <Briefcase className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Project Management',
    href: '/client/dashboard/hiring',
    icon: (
      <Users className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Bookings',
    href: '/filmmaker/dashboard/bookings',
    icon: (
      <CalendarCheck className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
    matchPrefixes: ['/filmmaker/dashboard/applications'],
  },
  {
    label: 'Messages',
    href: '/filmmaker/dashboard/messages',
    icon: (
      <MessageSquare className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
    matchPrefixes: ['/client/dashboard/messages'],
  },
  {
    label: 'Pitching Room',
    href: '/filmmaker/dashboard/pitching-room',
    icon: (
      <Presentation className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
    matchPrefixes: ['/client/dashboard/pitching-room'],
  },
  {
    label: 'Invoices',
    href: '/dashboard/invoices',
    icon: (
      <ReceiptPoundSterling className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
    matchPrefixes: ['/filmmaker/dashboard/invoices', '/client/dashboard/invoices'],
  },
  {
    label: 'Profile',
    href: '/filmmaker/dashboard/profile',
    icon: (
      <UserCircle className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
    matchPrefixes: ['/client/dashboard/account'],
  },
];

export function UnifiedAnimatedSidebar() {
  const location = useLocation();
  const { signOut } = useAuth();
  const { isOpen, setIsOpen } = useSidebarState();

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [signOut]);

  const isActiveLink = useCallback(
    (link: NavigationLink) => {
      const { href, matchPrefixes } = link;
      // Exact match for the calendar/home route
      if (href === '/filmmaker/dashboard') {
        return location.pathname === href || location.pathname === '/client/dashboard';
      }
      if (location.pathname.startsWith(href)) return true;
      if (matchPrefixes?.some((p) => location.pathname.startsWith(p))) return true;
      return false;
    },
    [location.pathname]
  );

  return (
    <Sidebar open={isOpen} setOpen={setIsOpen}>
      <SidebarBody className="justify-between gap-10 pt-4">
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          {isOpen ? <Logo /> : <LogoIcon />}

          {/* Tier Badge */}
          <div className="mt-6">
            <TierBadge isOpen={isOpen} />
          </div>

          <div className="mt-8 flex flex-col gap-2">
            {NAVIGATION_LINKS.map((link, idx) => {
              const isActive = isActiveLink(link);
              return (
                <SidebarLink
                  key={idx}
                  index={idx}
                  link={{
                    label: link.label,
                    href: link.href,
                    icon: link.icon,
                  }}
                  className={cn(
                    isActive
                      ? 'bg-primary/10 dark:bg-primary/20 text-primary font-semibold shadow-sm'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/5',
                    'rounded-xl'
                  )}
                />
              );
            })}
          </div>
        </div>
        <div className="mb-4 space-y-4">
          {/* No DashboardToggle — dual users see all items already */}

          {isOpen && (
            <div className="px-2 py-2">
              <ThemeToggleInline />
            </div>
          )}

          <div
            onClick={handleSignOut}
            role="button"
            tabIndex={0}
            className="cursor-pointer"
          >
            <SidebarLink
              link={{
                label: 'Logout',
                href: '#',
                icon: (
                  <div className="flex items-center justify-center h-10 w-10">
                    <LogOut className="text-red-500 h-5 w-5 flex-shrink-0" />
                  </div>
                ),
              }}
              className="text-red-500 px-2 hover:bg-red-500/5 dark:hover:bg-red-500/10 rounded-md"
            />
          </div>
        </div>
      </SidebarBody>
    </Sidebar>
  );
}

const Logo = () => (
  <Link
    to="/filmmaker/dashboard"
    className="font-normal flex items-center text-sm py-1 relative z-20 group"
  >
    <div className="flex items-center">
      <motion.img
        src="/lovable-uploads/91064b4e-0f45-41a0-8966-0416d131780a.png"
        alt="FindMyFilmmaker Logo"
        className="h-8 w-8 flex-shrink-0 rounded-md object-contain"
        whileHover={{ rotate: 360, scale: 1.1 }}
        transition={{ duration: 0.5, type: 'spring' }}
      />
      <motion.span
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
        className="font-medium text-black dark:text-white whitespace-pre ml-3"
      >
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          FindMyFilmmaker{' '}
        </motion.span>
      </motion.span>
    </div>
  </Link>
);

const LogoIcon = () => (
  <Link
    to="/filmmaker/dashboard"
    className="font-normal flex justify-center items-center text-sm py-1 relative z-20"
  >
    <motion.img
      src="/lovable-uploads/91064b4e-0f45-41a0-8966-0416d131780a.png"
      alt="FindMyFilmmaker Logo"
      className="h-8 w-8 flex-shrink-0 rounded-md object-contain"
      whileHover={{ rotate: 360, scale: 1.15 }}
      whileTap={{ scale: 0.9 }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 300 }}
    />
  </Link>
);
